import t from "@/lang/lang";
import {
  FlyExterminatorMaintenance,
  FlyExterminatorMaintenanceCohort,
  MaintenancePriceList,
} from "@/services/backend/qpm/calculations/types/calculation";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/shared/components/ui/table";

type Props = {
  flyExterminatorMaintenance: FlyExterminatorMaintenance;
};

export function FlyExterminatorMaintenanceOverviewComponent({
  flyExterminatorMaintenance,
}: Props) {
  const maintenance = normalizeFlyExterminatorMaintenance(
    flyExterminatorMaintenance,
  );
  const cohorts = maintenance.cohorts ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Wartung von Fluginsektenvernichter")}</CardTitle>
      </CardHeader>
      <CardContent>
        {cohorts.length === 0 ? (
          <div className="text-muted-foreground text-sm">
            {t("Keine FIV-Wartung vorhanden")}
          </div>
        ) : (
          <Table>
            <TableBody>
              {cohorts.map((cohort, idx) => (
                <TableRow key={cohortSectionKey(cohort, idx)}>
                  <TableCell className="font-semibold">
                    {t("Wartung von")}{" "}
                    <span className="text-base font-semibold">
                      {cohort.priceList?.totalAmount ?? 0}
                    </span>{" "}
                    <span className="text-base font-semibold">
                      {t("Stück")}
                    </span>{" "}
                    {MonitorTypeUtil.toLongString(MonitorType.FlyExterminator)}
                  </TableCell>
                  <TableCell>
                    {t("Anzahl Maßnahmen / Jahr")}{" "}
                    <span className="text-base font-semibold">
                      {cohort.totalAmountOfMeasures ?? 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-end font-medium">
                    {t("Preis pro Maßnahme")}:{" "}
                    <span className="text-base font-semibold">
                      {(
                        cohort.priceList?.totalCostPerMeasure ?? 0
                      ).toLocaleString("de-DE", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function cohortSectionKey(
  cohort: FlyExterminatorMaintenanceCohort,
  idx: number,
) {
  const ids = cohort.serviceGroupIds ?? [];
  return `${idx}-${cohort.totalAmountOfMeasures}-${ids.length}-${[...ids]
    .sort()
    .join("-")}`;
}

function normalizeFlyExterminatorMaintenance(
  raw: FlyExterminatorMaintenance | undefined,
): FlyExterminatorMaintenance {
  if (!raw || typeof raw !== "object") {
    return { cohorts: [] };
  }

  const record = raw as unknown as Record<string, unknown>;
  const cohortsRaw =
    (record.cohorts as unknown[]) ?? (record.Cohorts as unknown[]);

  const cohorts = Array.isArray(cohortsRaw)
    ? cohortsRaw.map((cohort) => {
        const c = cohort as Record<string, unknown>;
        return {
          totalAmountOfMeasures:
            (c.totalAmountOfMeasures as number) ??
            (c.TotalAmountOfMeasures as number) ??
            0,
          serviceGroupIds:
            (c.serviceGroupIds as string[]) ??
            (c.ServiceGroupIds as string[]) ??
            [],
          priceList:
            (c.priceList as MaintenancePriceList) ??
            (c.PriceList as MaintenancePriceList),
          laborPricePerDevicePerMeasure:
            (c.laborPricePerDevicePerMeasure as number) ??
            (c.LaborPricePerDevicePerMeasure as number) ??
            0,
          calculatedLaborPricePerDevicePerMeasure:
            (c.calculatedLaborPricePerDevicePerMeasure as number) ??
            (c.CalculatedLaborPricePerDevicePerMeasure as number) ??
            0,
        } as FlyExterminatorMaintenanceCohort;
      })
    : [];

  return {
    ...raw,
    cohorts,
  };
}
